import React, { useState, useEffect, useCallback } from 'react';
import { Syringe, Stethoscope, Scissors, Calendar, Search, ChevronDown, X, Package, Users, PawPrint, Box, DoorOpen, ClipboardList, Layers, ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { getOperations, getAllLogs, getSmsHistory } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import PetAvatar from '../components/PetAvatar';

const ALL_TABS = [
  { value: 'all', label: 'Tümü', icon: Layers },
  { value: 'vaccine', label: 'Aşı', icon: Syringe },
  { value: 'treatment', label: 'Tedavi', icon: Stethoscope },
  { value: 'service', label: 'Hizmet', icon: Scissors },
  { value: 'product', label: 'Ürün', icon: Package },
  { value: 'customer', label: 'Müşteri', icon: Users },
  { value: 'pet', label: 'Hasta', icon: PawPrint },
  { value: 'stock', label: 'Stok', icon: Box },
  { value: 'boarding', label: 'Konaklama', icon: DoorOpen },
  { value: 'status', label: 'Durum', icon: ClipboardList },
  { value: 'sms', label: 'SMS', icon: MessageSquare },
];

const LOG_TABS = ['customer', 'pet', 'stock', 'boarding', 'status', 'sms'];

const ACTION_LABELS = {
  customer_created: 'Müşteri Eklendi',
  customer_updated: 'Müşteri Güncellendi',
  pet_created: 'Hasta Eklendi',
  pet_updated: 'Hasta Güncellendi',
  product_created: 'Ürün Eklendi',
  product_updated: 'Ürün Güncellendi',
  product_deleted: 'Ürün Silindi',
  stock_updated: 'Stok Güncellendi',
  room_created: 'Oda Eklendi',
  room_updated: 'Oda Güncellendi',
  room_deleted: 'Oda Silindi',
  cage_created: 'Kafes Eklendi',
  cage_updated: 'Kafes Güncellendi',
  cage_deleted: 'Kafes Silindi',
  check_in: 'Giriş Yapıldı',
  check_out: 'Çıkış Yapıldı',
  record_deleted: 'Kayıt Silindi',
  // SMS
  sms_vaccine_reminder: 'Aşı Hatırlatma',
  sms_treatment_reminder: 'Tedavi Hatırlatma',
  sms_appointment_confirmed: 'Randevu Onayı',
  sms_appointment_rejected: 'Randevu Reddi',
  sms_balance_reminder: 'Bakiye Hatırlatma',
  sms_boarding_checkout: 'Konaklama Bildirimi',
  sms_birthday_greeting: 'Doğum Günü',
};

const ACTION_COLORS = {
  customer_created: 'bg-blue-50 text-blue-700',
  customer_updated: 'bg-blue-50 text-blue-600',
  pet_created: 'bg-green-50 text-green-700',
  pet_updated: 'bg-green-50 text-green-600',
  product_created: 'bg-purple-50 text-purple-700',
  product_updated: 'bg-purple-50 text-purple-600',
  product_deleted: 'bg-red-50 text-red-600',
  stock_updated: 'bg-amber-50 text-amber-700',
  room_created: 'bg-blue-50 text-blue-700',
  room_updated: 'bg-blue-50 text-blue-600',
  room_deleted: 'bg-red-50 text-red-700',
  cage_created: 'bg-indigo-50 text-indigo-700',
  cage_updated: 'bg-indigo-50 text-indigo-600',
  cage_deleted: 'bg-red-50 text-red-600',
  check_in: 'bg-green-50 text-green-700',
  check_out: 'bg-orange-50 text-orange-700',
  record_deleted: 'bg-red-50 text-red-700',
  // SMS
  sms_sent: 'bg-green-50 text-green-700',
  sms_failed: 'bg-red-50 text-red-700',
  sms_skipped: 'bg-orange-50 text-orange-700',
};

const OP_BADGE_COLORS = {
  vaccine: 'bg-green-50 text-green-700',
  treatment: 'bg-[#6b6de3]/10 text-[#6b6de3]',
  service: 'bg-orange-50 text-orange-700',
  product: 'bg-purple-50 text-purple-700',
};

const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);

const formatDT = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function getWeekRange(offset = 0) {
  const now = new Date();
  const day = now.getDay();
  const mon = new Date(now);
  mon.setDate(now.getDate() - (day === 0 ? 6 : day - 1) + offset * 7);
  mon.setHours(0, 0, 0, 0);
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  sun.setHours(23, 59, 59, 999);
  return { start: mon, end: sun };
}

function formatWeekLabel(offset) {
  if (offset === 0) return 'Bu Hafta';
  if (offset === -1) return 'Geçen Hafta';
  const { start, end } = getWeekRange(offset);
  const fmt = (d) => d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' });
  return `${fmt(start)} - ${fmt(end)}`;
}

const Records = () => {
  const [operations, setOperations] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);

  const PAGE_SIZE = 200;

  const loadData = useCallback(async (append = false, skipVal = 0) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);

    try {
      const { start, end } = getWeekRange(weekOffset);
      const params = {
        skip: skipVal,
        limit: PAGE_SIZE,
        date_from: start.toISOString().split('T')[0],
        date_to: end.toISOString().split('T')[0],
      };
      if (search.trim()) params.search = search.trim();

      const promises = [
        getOperations(params),
        !append ? getAllLogs(500) : Promise.resolve(null),
        !append ? getSmsHistory({ limit: 200 }) : Promise.resolve(null),
      ];

      const [opsRes, logsRes, smsRes] = await Promise.all(promises);

      const newOps = opsRes.data;
      if (append) {
        setOperations(prev => [...prev, ...newOps]);
      } else {
        setOperations(newOps);
      }
      setHasMore(newOps.length >= PAGE_SIZE);

      if (logsRes || smsRes) {
        const logs = logsRes ? logsRes.data : activityLogs.filter(l => l.category !== 'sms');
        const smsLogs = smsRes?.data?.records?.map(r => ({
          category: 'sms',
          action: `sms_${r.status}`,
          target_name: r.customer_name || '',
          user_name: r.sms_type_label || r.sms_type || '',
          created_at: r.created_at,
          details: {
            customer_name: r.customer_name,
            customer_phone: r.customer_phone,
            pet_name: r.pet_name,
            message: r.message,
            status: r.status,
            error: r.error,
            sms_type: r.sms_type,
            sms_type_label: r.sms_type_label,
          },
        })) || [];
        setActivityLogs([...logs, ...smsLogs]);
      }
    } catch (error) {
      console.error('Error loading records:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [search, weekOffset]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleLoadMore = () => { loadData(true, operations.length); };

  const isLogTab = LOG_TABS.includes(activeTab);

  // Filter operations for operation tabs
  const filteredOperations = activeTab === 'all'
    ? operations
    : operations.filter(op => op.type === activeTab);

  const groupedOperations = filteredOperations.reduce((groups, op) => {
    const date = op.date.split('T')[0];
    if (!groups[date]) groups[date] = [];
    groups[date].push(op);
    return groups;
  }, {});

  // Filter logs for log tabs (by week)
  const { start: weekStart, end: weekEnd } = getWeekRange(weekOffset);

  const filteredLogs = isLogTab
    ? activityLogs.filter(l => {
        if (l.category !== activeTab) return false;
        const d = new Date(l.created_at);
        return d >= weekStart && d <= weekEnd;
      })
    : [];

  const searchFilteredLogs = search.trim()
    ? filteredLogs.filter(l =>
        (l.target_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.user_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.details?.customer_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : filteredLogs;

  const groupedLogs = searchFilteredLogs.reduce((groups, log) => {
    const date = (log.created_at || '').split('T')[0];
    if (!groups[date]) groups[date] = [];
    groups[date].push(log);
    return groups;
  }, {});

  if (loading) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm sticky top-0 z-10">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">Kayıtlar</h1>

        {/* Filter Panel */}
        <div className="bg-[#F2F2F7] rounded-2xl p-1.5 space-y-1.5">
          {/* Row 1: Week Selector */}
          <div className="flex items-center justify-between bg-white rounded-xl px-2 py-1.5">
            <button onClick={() => setWeekOffset(w => w - 1)} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93]" data-testid="week-prev">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setWeekOffset(0)} className={`text-xs font-semibold px-2 py-0.5 rounded-lg transition-colors ${weekOffset === 0 ? 'text-[#6b6de3] bg-[#6b6de3]/10' : 'text-gray-700 hover:bg-[#F2F2F7]'}`} data-testid="week-label">
              {formatWeekLabel(weekOffset)}
            </button>
            <button onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93] disabled:opacity-30" data-testid="week-next">
              <ChevronRight size={16} />
            </button>
          </div>
          {/* Row 2: Search + Tab Dropdown */}
          <div className="flex items-center gap-1.5">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
              <Input
                type="text"
                placeholder="Ara..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 pr-7 h-9 text-xs bg-white border-0 rounded-xl"
                data-testid="records-search-input"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#8E8E93]">
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="relative">
              <button
                onClick={() => setTabDropdownOpen(!tabDropdownOpen)}
                className="flex items-center gap-1.5 h-9 px-3 bg-white rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                data-testid="tab-dropdown-btn"
              >
                {(() => { const t = ALL_TABS.find(t => t.value === activeTab); const Icon = t?.icon; return Icon ? <Icon size={13} className="text-[#6b6de3]" /> : null; })()}
                {ALL_TABS.find(t => t.value === activeTab)?.label || 'Tümü'}
                <ChevronDown size={14} className={`text-[#8E8E93] transition-transform ${tabDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {tabDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setTabDropdownOpen(false)} />
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[160px]" data-testid="tab-dropdown-menu">
                    {ALL_TABS.map(tab => {
                      const Icon = tab.icon;
                      const active = activeTab === tab.value;
                      return (
                        <button
                          key={tab.value}
                          onClick={() => { setActiveTab(tab.value); setTabDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${active ? 'text-[#6b6de3] bg-[#6b6de3]/5' : 'text-gray-700 hover:bg-[#F2F2F7]'}`}
                          data-testid={`tab-${tab.value}`}
                        >
                          <Icon size={14} className={active ? 'text-[#6b6de3]' : 'text-[#8E8E93]'} />
                          {tab.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {/* Unified record list for all tabs */}
        {!isLogTab ? (
          Object.keys(groupedOperations).length > 0 ? (
            <>
              {Object.entries(groupedOperations).map(([date, ops]) => (
                <div key={date} className="animate-fade-in">
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <Calendar size={14} className="text-[#8E8E93]" />
                    <span className="text-xs font-medium text-[#8E8E93] uppercase tracking-wider">
                      {new Date(date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                  </div>
                  <div className="ios-card space-y-2">
                    {ops.map((op) => (
                      <RecordCard key={op.id} item={{
                        id: op.id,
                        petName: op.pet_name,
                        petSpecies: op.pet_species,
                        petGender: op.pet_gender,
                        customerName: op.customer_name,
                        customerPhone: op.customer_phone,
                        userName: op.user_name,
                        actionLabel: op.name,
                        actionColor: OP_BADGE_COLORS[op.type] || 'bg-gray-100 text-gray-600',
                        amount: op.price,
                        nextDue: op.next_due_date,
                        date: op.date,
                      }} />
                    ))}
                  </div>
                </div>
              ))}
              {hasMore && (
                <Button onClick={handleLoadMore} disabled={loadingMore} variant="outline"
                  className="w-full h-12 rounded-full border-[#6b6de3] text-[#6b6de3] hover:bg-[#6b6de3]/5" data-testid="load-more-btn">
                  {loadingMore ? 'Yükleniyor...' : <><ChevronDown size={18} className="mr-2" /> Daha Fazla Yükle</>}
                </Button>
              )}
            </>
          ) : (
            <div className="ios-card text-center py-12">
              <Calendar size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">Bu hafta kayıt bulunamadı</p>
            </div>
          )
        ) : (
          Object.keys(groupedLogs).length > 0 ? (
            Object.entries(groupedLogs).map(([date, logs]) => (
              <div key={date} className="animate-fade-in">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <Calendar size={14} className="text-[#8E8E93]" />
                  <span className="text-xs font-medium text-[#8E8E93] uppercase tracking-wider">
                    {new Date(date).toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
                  </span>
                </div>
                <div className="ios-card space-y-2">
                  {logs.map((log) => {
                    const isStatus = log.log_type === 'status';
                    const isSms = log.category === 'sms';
                    const smsStatusLabels = { sms_sent: 'Gönderildi', sms_failed: 'Başarısız', sms_skipped: 'Atlandı' };
                    return (
                      <RecordCard key={log.id || Math.random()} item={{
                        id: log.id,
                        petName: isSms ? (log.details?.pet_name || log.target_name) : (log.target_name || log.pet_name),
                        petSpecies: log.details?.pet_species || log.details?.species,
                        petGender: log.details?.pet_gender,
                        customerName: log.details?.customer_name || log.customer_name,
                        customerPhone: log.details?.customer_phone || log.details?.phone,
                        userName: isSms ? (log.details?.sms_type_label || log.user_name) : (log.user_name || log.changed_by_name),
                        actionLabel: isSms ? (smsStatusLabels[log.action] || log.action) : isStatus ? `${statusLabel(log.old_status)} → ${statusLabel(log.new_status)}` : (ACTION_LABELS[log.action] || log.action || ''),
                        actionColor: isSms ? (ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600') : isStatus ? 'bg-blue-50 text-blue-700' : (ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-600'),
                        amount: log.details?.total_cost ?? log.details?.price,
                        extraInfo: isSms ? (log.details?.message ? log.details.message.substring(0, 80) + (log.details.message.length > 80 ? '...' : '') : null) : (log.details?.amount != null ? `Miktar: ${log.details.amount > 0 ? '+' : ''}${log.details.amount}${log.details.new_stock != null ? ` (Yeni: ${log.details.new_stock})` : ''}` : null),
                        date: log.created_at,
                      }} />
                    );
                  })}
                </div>
              </div>
            ))
          ) : (
            <div className="ios-card text-center py-12">
              <ClipboardList size={48} className="text-[#C7C7CC] mx-auto mb-3" />
              <p className="text-[#8E8E93]">Kayıt bulunamadı</p>
            </div>
          )
        )}
      </main>

      <BottomNav />
    </div>
  );
};

function RecordCard({ item }) {
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-[#F2F2F7] rounded-xl" data-testid={`record-${item.id}`}>
      <PetAvatar species={item.petSpecies} gender={item.petGender} size={12} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-[9px] font-semibold px-1.5 py-px rounded-full shrink-0 ${item.actionColor}`}>
            {item.actionLabel}
          </span>
          {item.petName && <span className="text-xs font-medium text-gray-900 truncate">{item.petName}</span>}
        </div>
        <div className="flex items-center gap-1.5 text-[10px] truncate">
          {item.customerName && <span className="text-[#6b6de3]">{item.customerName}</span>}
          {item.customerPhone && <><span className="text-[#C7C7CC]">·</span><span className="text-[#8E8E93]">{item.customerPhone}</span></>}
          {item.extraInfo && <><span className="text-[#C7C7CC]">·</span><span className="text-[#8E8E93]">{item.extraInfo}</span></>}
        </div>
      </div>
      <div className="text-right shrink-0">
        {item.amount != null && <p className="text-xs font-bold text-[#FF9500]">{formatCurrency(item.amount)}</p>}
        {item.nextDue && <p className="text-[9px] text-[#FF9500]">{`→ ${new Date(item.nextDue).toLocaleDateString('tr-TR')}`}</p>}
        {item.userName && <p className="text-[9px] text-[#C7C7CC]">{item.userName}</p>}
      </div>
    </div>
  );
}

function statusLabel(s) {
  switch (s) {
    case 'pending': return 'Bekliyor';
    case 'informed': return 'Bilgilendirildi';
    case 'completed': return 'Tamamlandı';
    case 'cancelled': return 'İptal';
    default: return s || '?';
  }
}

export default Records;
